import { useQuery, useQueryClient } from 'react-query';
import { useSelector } from 'react-redux';

import { getOrderChangeRequestApi } from '@api/publishers.api';
import { getCurrentWorkspaceId } from '@redux/selectors/workspaces.selectors';
import queryCacheKeys from '@constants/queryCacheKeys.constants';
import { ChangeRequestType } from '@ts/publishers.types';
import { QueryObjectHookType } from '@ts/common.types';

export default function useOrderChangeRequest(
  publisherOrderId: number
): QueryObjectHookType<ChangeRequestType> {
  const queryClient = useQueryClient();
  const workspaceId = useSelector(getCurrentWorkspaceId);

  const composeKey = (id: number) => [queryCacheKeys.publisherOrderChangeRequest, workspaceId, id];

  const key = composeKey(publisherOrderId);

  const { data, isLoading, isError, error, refetch } = useQuery<ChangeRequestType>({
    queryKey: key,
    queryFn: () => getOrderChangeRequestApi(workspaceId, publisherOrderId),
    refetchOnWindowFocus: false,
    retry: false,
    enabled: !!workspaceId && publisherOrderId != null && publisherOrderId > 0,
  });

  const updateInCache = (payloadId: number, payload: ChangeRequestType) => {
    queryClient.setQueryData<ChangeRequestType>(composeKey(payloadId), (cachedData) => {
      return { ...cachedData, ...payload };
    });
  };

  const evictCacheByKey = (id: number) => queryClient.removeQueries(composeKey(id));

  return {
    item: data,
    refetch,
    // Deliberately NOT `|| isFetching`: a background refetch of already-cached data
    // would otherwise swap the whole rendered conversation for a spinner on every reopen.
    isLoading,
    isError,
    error,
    update: updateInCache,
    evictByKey: evictCacheByKey,
  };
}
